import {
    ContractExternalDataCrossLinkDto
} from "../../../../../../../ci/model/dto/api/contract-external-data-cross-link.dto";
import {CmEdCrossLinkPt} from "../interface/cm-ed-cross-link-pt.interface";

/**
 * The accessible name of ONE cross-link chip — the SINGLE definition on this sheet.
 *
 * Two places render the chips: the shared Deckung card and the "Alle Daten" sheet, which draws its
 * own row sub-headings and therefore cannot go through the card. The German wording, the role and the
 * separator are stated here so the two can never announce a person differently.
 *
 * A function rather than a precomputed map: PrimeNG's binding directive compares the pass-through
 * DEEPLY before touching the DOM, so a fresh object per change-detection pass costs an allocation and
 * nothing else.
 *
 * @param link the edge the chip stands for
 * @return the pass-through to bind on the chip's `[pt]`
 */
export function buildCrossLinkPt(link: ContractExternalDataCrossLinkDto): CmEdCrossLinkPt {

    const kind: string = link.kind === 'person' ? 'Versicherte Person: ' : 'Versichertes Objekt: ';

    return {
        root: {
            role: 'img',
            'aria-label': kind + link.label,
        },
    };
}

/**
 * The severity of ONE cross-link chip — the SINGLE definition on this sheet.
 *
 * `info` for a person, `secondary` for an object. Colour is the fastest way to tell "was ist bei Tim
 * versichert?" from "was ist am Haus versichert?" in a row of chips, and both severities are already
 * the app's own most-used tags (secondary 676, info 221 across features/).
 *
 * It lives beside {@link buildCrossLinkPt} for exactly the reason that function does: the same two
 * places render the chips, and a chip whose COLOUR says "person" in the Deckung card but not in
 * "Alle Daten" is the same defect as one whose accessible name differs — the kind would then be a
 * property of the view instead of a property of the edge.
 *
 * Driven by `link.kind`, which is DATA the delivery carries, never inferred from the label, which is
 * a partner-supplied string. Colour is never the ONLY carrier: the kind is in the chip's accessible
 * name and in its tooltip, so a colour-blind or screen-reader user loses nothing.
 *
 * @param link the edge the chip stands for
 * @return the severity to bind on the chip's `[severity]`
 */
export function crossLinkSeverity(link: ContractExternalDataCrossLinkDto): 'info' | 'secondary' {
    return link?.kind === 'person' ? 'info' : 'secondary';
}
